import React, { createContext, useContext, useEffect, useMemo } from "react";
import * as signalR from "@microsoft/signalr";
import {
  getPortalNotificationConnection,
  resetPortalNotificationConnection,
  createZipProgressConnection,
} from "../signalr/signalRConnection";
import { stopConnection } from "../utils/SignalRConnectionManager";
import { getOrCreateManager } from "../utils/SignalRManagerRegistry";

interface SignalRContextValue {
  notificationConnection: signalR.HubConnection | null;
  zipProgressConnection: signalR.HubConnection | null;
  stopAllConnections: () => Promise<void>;
  restartConnections: (newSessionValue: string) => Promise<{
    notificationConnection: signalR.HubConnection | null;
    zipProgressConnection: signalR.HubConnection | null;
  }>;
}

const SignalRContext = createContext<SignalRContextValue>({
  notificationConnection: null,
  zipProgressConnection: null,
  stopAllConnections: async () => Promise.resolve(),
  restartConnections: async () => ({
    notificationConnection: null,
    zipProgressConnection: null,
  }),
});

export const useSignalRConnections = () => useContext(SignalRContext);

export const SignalRProvider: React.FC<{
  userId: number | null;
  sessionValue: string | null;
  children: React.ReactNode;
}> = ({ userId, sessionValue, children }) => {
  // Create connections
  const notificationConnection = useMemo(() => {
    if (sessionValue) {
      return getPortalNotificationConnection(sessionValue);
    }
    return null;
  }, [sessionValue]);

  const zipProgressConnection = useMemo(() => {
    return createZipProgressConnection();
  }, []);

  useEffect(() => {
    console.log(
      "[SignalRProvider] useEffect fired — userId:",
      userId,
      "sessionValue:",
      sessionValue,
      "notificationConnection:",
      !!notificationConnection,
    );
    if (userId && userId > 0) {
      (async () => {
        try {
          if (notificationConnection) {
            const manager = getOrCreateManager(notificationConnection);
            await manager.start();
            console.log(
              "[SignalRProvider] notificationConnection state after start:",
              notificationConnection.state,
              "connectionId:",
              notificationConnection.connectionId,
            );
          }
          if (zipProgressConnection) {
            const manager = getOrCreateManager(zipProgressConnection);
            await manager.start();
            console.log(
              "[SignalRProvider] zipProgressConnection state after start:",
              zipProgressConnection.state,
            );
          }
        } catch (err) {
          console.error("Error starting SignalR connections:", err);
        }
      })();
      return () => {
        // Only stop fully-connected connections. Skipping Connecting state
        // prevents React StrictMode's mount-cleanup-mount cycle from aborting
        // an in-progress negotiation.
        if (notificationConnection?.state === signalR.HubConnectionState.Connected) {
          void stopConnection(notificationConnection);
        }
        if (zipProgressConnection?.state === signalR.HubConnectionState.Connected) {
          void stopConnection(zipProgressConnection);
        }
      };
    }
  }, [userId, notificationConnection, zipProgressConnection]);

  const stopAllConnections = async () => {
    if (notificationConnection) {
      await stopConnection(notificationConnection);
    }
    if (zipProgressConnection) {
      await stopConnection(zipProgressConnection);
    }
  };

  const restartConnections = async (newSessionValue: string) => {
    await stopAllConnections();

    // Reset the singleton so a new connection is created with the new session value
    resetPortalNotificationConnection();

    // Recreate connections
    const newNotificationConn = getPortalNotificationConnection(newSessionValue);
    const newZipConn = createZipProgressConnection();

    // Start connections safely using the registry
    const notificationManager = getOrCreateManager(newNotificationConn);
    const zipManager = getOrCreateManager(newZipConn);

    await notificationManager.start();
    await zipManager.start();

    // Return new connections so caller can update state if needed
    return {
      notificationConnection: newNotificationConn,
      zipProgressConnection: newZipConn,
    };
  };

  return (
    <SignalRContext.Provider
      value={{
        notificationConnection,
        zipProgressConnection,
        stopAllConnections,
        restartConnections,
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
};
